import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type CascaderOption, toCascaderOptions } from '@/features/productSearch/vestiaire/catalogTree';
import {
  type VestiaireUrlImportState,
  useVestiaireUrlImport,
} from '@/features/productSearch/vestiaire/useVestiaireUrlImport';
import {
  EMPTY_VESTIAIRE_FORM,
  type VestiaireFormErrors,
  type VestiaireFormState,
  buildVestiaireCriteria,
  countMissingFilterCategories,
  validateVestiaireForm,
  vestiaireFilterToForm,
} from '@/features/productSearch/vestiaire/vestiaireCriteria';
import type { VestiaireSearchCriteriaInput } from '@/shared/api/types/generated';
import { useGetVestiaireCatalogTreeQuery, useGetVestiaireFilterOptionsQuery } from '@/shared/hooks/queries/generated';

// ============================================================================
// État du formulaire de recherche Vestiaire : arbre des catégories, préremplissage
// depuis un vestiaireFilter stocké (édition, relance), titres des ids des Select
// asynchrones (marque, modèle, taille).
// ============================================================================

export type VestiaireAsyncFilterType = 'brand' | 'model' | 'size';

export type VestiaireLabels = Record<VestiaireAsyncFilterType, Map<number, string>>;

export interface UseVestiaireSearchFormOptions {
  initialFilter?: Record<string, unknown> | null;
  /**
   * Coupe les requêtes (arbre, résolution des titres) tant que le formulaire ne sert pas. Défaut
   * true. Le préremplissage attend l'arbre : il se fait de lui-même à son arrivée après activation.
   */
  enabled?: boolean;
}

export interface VestiaireSubmitDraft {
  criteria: VestiaireSearchCriteriaInput;
  errors: VestiaireFormErrors;
  /** Critère issu (au moins en partie) d'un import d'URL. */
  fromUrl: boolean;
}

export interface VestiaireSearchForm {
  form: VestiaireFormState;
  setField: <K extends keyof VestiaireFormState>(key: K, value: VestiaireFormState[K]) => void;
  /** Remplace tout le formulaire par un critère importé (import d'URL VC), mêmes clés que le critère ; rend le formulaire posé. */
  importCriteria: (criteria: Record<string, unknown>) => VestiaireFormState;
  reset: () => void;
  criteria: VestiaireSearchCriteriaInput;
  errors: VestiaireFormErrors;
  treeOptions: CascaderOption[];
  treeLoading: boolean;
  /** Arbre chargé : préalable à l'import d'URL (chemins de catégories). */
  treeReady: boolean;
  /** Arbre en échec : le panneau l'affiche, sinon il resterait vide sans explication (et l'édition aussi). */
  treeError: boolean;
  labels: VestiaireLabels;
  rememberLabels: (type: VestiaireAsyncFilterType, options: { id: number; title: string }[]) => void;
  /** Import d'URL VC (champ du panneau, import implicite à l'envoi). */
  urlImport: VestiaireUrlImportState;
  /**
   * Envoi : importe d'abord l'URL collée mais pas importée (comme Vinted), puis rend le critère et les
   * erreurs du formulaire EFFECTIF (l'importé, l'état React n'étant à jour qu'au rendu suivant) ;
   * null si l'URL n'a pas pu être lue (erreur sous le champ).
   */
  prepareSubmit: () => Promise<VestiaireSubmitDraft | null>;
  /** Catégories du filtre stocké absentes de l'arbre actuel (écartées du préremplissage). */
  missingCategoryCount: number;
}

const EMPTY_LABELS: VestiaireLabels = { brand: new Map(), model: new Map(), size: new Map() };

export const useVestiaireSearchForm = ({
  initialFilter,
  enabled = true,
}: UseVestiaireSearchFormOptions = {}): VestiaireSearchForm => {
  const readOnlyPrefill = initialFilter != null;

  const {
    data: treeData,
    isLoading: treeLoading,
    isError: treeError,
  } = useGetVestiaireCatalogTreeQuery(undefined, {
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  const tree = treeData?.getVestiaireCatalogTree;
  const treeOptions = useMemo(() => toCascaderOptions(tree ?? []), [tree]);

  const [form, setForm] = useState<VestiaireFormState>(EMPTY_VESTIAIRE_FORM);

  // Préremplissage UNE seule fois, dès que l'arbre est là (les chemins du Cascader en
  // dépendent) : la garde empêche un rechargement de l'arbre d'écraser une saisie.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !initialFilter || !tree) return;
    prefilled.current = true;
    setForm(vestiaireFilterToForm(initialFilter, tree));
  }, [initialFilter, tree]);

  const setField = useCallback(<K extends keyof VestiaireFormState>(key: K, value: VestiaireFormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  }, []);

  // Import d'URL : le formulaire entier est remplacé (comme l'import Vinted). L'arbre est requis pour
  // les chemins du Cascader : l'import est refusé tant qu'il n'est pas là (`treeReady`).
  const importCriteria = useCallback(
    (criteria: Record<string, unknown>) => {
      const imported = vestiaireFilterToForm(criteria, tree ?? []);
      setForm(imported);
      return imported;
    },
    [tree],
  );

  // Retour à l'état initial : le filtre préremplit s'il est fourni et l'arbre chargé ; sinon
  // formulaire vide, et la garde est réarmée pour que le préremplissage se fasse à l'arrivée de l'arbre.
  const resetForm = useCallback(() => {
    if (initialFilter && tree) {
      prefilled.current = true;
      setForm(vestiaireFilterToForm(initialFilter, tree));
    } else {
      prefilled.current = false;
      setForm(EMPTY_VESTIAIRE_FORM);
    }
  }, [initialFilter, tree]);

  const criteria = useMemo(() => buildVestiaireCriteria(form), [form]);
  const missingCategoryCount = useMemo(
    () => (tree ? countMissingFilterCategories(initialFilter, tree) : 0),
    [initialFilter, tree],
  );
  const errors = useMemo(() => validateVestiaireForm(form), [form]);

  const [labels, setLabels] = useState<VestiaireLabels>(EMPTY_LABELS);
  const rememberLabels = useCallback((type: VestiaireAsyncFilterType, options: { id: number; title: string }[]) => {
    setLabels((previous) => {
      const known = previous[type];
      const fresh = options.filter((option) => known.get(option.id) !== option.title);
      // Rien de nouveau : même objet, aucun rendu déclenché.
      if (fresh.length === 0) return previous;
      const next = new Map(known);
      for (const option of fresh) next.set(option.id, option.title);
      return { ...previous, [type]: next };
    });
  }, []);

  const treeReady = tree != null;
  const urlImport = useVestiaireUrlImport({ importCriteria, rememberLabels, treeReady });
  const resetUrlImport = urlImport.reset;
  const prepareSubmit = async (): Promise<VestiaireSubmitDraft | null> => {
    const pending = await urlImport.importPending();
    if (pending.status === 'failed') return null;
    const effective = pending.status === 'imported' ? pending.form : form;
    return {
      criteria: buildVestiaireCriteria(effective),
      errors: validateVestiaireForm(effective),
      fromUrl: pending.status === 'imported' || urlImport.imported,
    };
  };

  // L'URL saisie est remise à zéro avec le formulaire (réouverture de la modale).
  const reset = useCallback(() => {
    resetForm();
    resetUrlImport();
  }, [resetForm, resetUrlImport]);

  // Résolution id → titre des valeurs stockées (édition) : un appel par dimension non vide.
  // Marques retenues ET exclues : un seul appel, un seul dictionnaire de titres (`labels.brand`).
  const storedBrandIds = useMemo(
    () => [...new Set([...form.brandIds, ...form.excludedBrandIds])],
    [form.brandIds, form.excludedBrandIds],
  );
  const brandTitles = useGetVestiaireFilterOptionsQuery(
    { filterType: 'brand', query: null, limit: null, ids: storedBrandIds },
    { enabled: enabled && readOnlyPrefill && storedBrandIds.length > 0, staleTime: Infinity },
  ).data?.getVestiaireFilterOptions;
  const modelTitles = useGetVestiaireFilterOptionsQuery(
    { filterType: 'model', query: null, limit: null, ids: form.modelIds },
    { enabled: enabled && readOnlyPrefill && form.modelIds.length > 0, staleTime: Infinity },
  ).data?.getVestiaireFilterOptions;
  const sizeTitles = useGetVestiaireFilterOptionsQuery(
    { filterType: 'size', query: null, limit: null, ids: form.sizeIds },
    { enabled: enabled && readOnlyPrefill && form.sizeIds.length > 0, staleTime: Infinity },
  ).data?.getVestiaireFilterOptions;

  useEffect(() => {
    if (brandTitles) rememberLabels('brand', brandTitles);
  }, [brandTitles, rememberLabels]);
  useEffect(() => {
    if (modelTitles) rememberLabels('model', modelTitles);
  }, [modelTitles, rememberLabels]);
  useEffect(() => {
    if (sizeTitles) rememberLabels('size', sizeTitles);
  }, [sizeTitles, rememberLabels]);

  return {
    form,
    setField,
    importCriteria,
    reset,
    criteria,
    errors,
    treeOptions,
    treeLoading,
    treeReady,
    treeError,
    labels,
    rememberLabels,
    urlImport,
    prepareSubmit,
    missingCategoryCount,
  };
};
